import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Check, UserPlus, Users } from 'lucide-react';
import { WidgetGroup, CanvasWidget } from './types';

interface WidgetGroupManagerProps {
  onClose: () => void;
  widgetGroups: WidgetGroup[];
  widgets: CanvasWidget[];
  selectedWidgetIds: string[];
  onCreateGroup: (group: Omit<WidgetGroup, 'id'>) => string;
  onUpdateGroup: (group: WidgetGroup) => void;
  onDeleteGroup: (groupId: string) => void;
  onAddToGroup: (widgetIds: string[], groupId: string, parentWidgetId?: string) => void;
  onRemoveFromGroup: (widgetIds: string[]) => void;
}

// Predefined color options for widget groups
const colorOptions = [
  { name: 'Green', value: '#38cd9a' },  // Primary
  { name: 'Blue', value: '#60a5fa' },
  { name: 'Purple', value: '#a78bfa' },
  { name: 'Pink', value: '#f472b6' },
  { name: 'Yellow', value: '#fbbf24' },
  { name: 'Cyan', value: '#0ea5e9' },
  { name: 'Orange', value: '#fb923c' },
  { name: 'Red', value: '#f43f5e' },
];

const WidgetGroupManager: React.FC<WidgetGroupManagerProps> = ({
  onClose,
  widgetGroups,
  widgets,
  selectedWidgetIds,
  onCreateGroup,
  onUpdateGroup,
  onDeleteGroup,
  onAddToGroup,
  onRemoveFromGroup
}) => {
  const [activeView, setActiveView] = useState<'groups' | 'create' | 'edit'>('groups');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState(colorOptions[0].value);
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [selectedParentWidgetId, setSelectedParentWidgetId] = useState<string | null>(null);
  
  // Get selected widgets
  const selectedWidgets = widgets.filter(widget => selectedWidgetIds.includes(widget.id));
  
  // Initialize edit form when a group is selected
  useEffect(() => {
    if (selectedGroupId && activeView === 'edit') {
      const group = widgetGroups.find(g => g.id === selectedGroupId);
      if (group) {
        setNewGroupName(group.name);
        setNewGroupColor(group.color);
        setNewGroupDescription(group.description || '');
        setSelectedParentWidgetId(group.parentWidgetId || null);
      }
    }
  }, [selectedGroupId, activeView, widgetGroups]);
  
  // Check if all selected widgets belong to the same group
  const getCommonGroupId = () => {
    if (selectedWidgets.length === 0) return null;
    
    const firstGroupId = selectedWidgets[0].groupId;
    const allSameGroup = selectedWidgets.every(w => w.groupId === firstGroupId);
    
    return allSameGroup ? firstGroupId : null;
  };
  
  const commonGroupId = getCommonGroupId();
  
  // Handle creating a new group
  const handleCreateGroup = () => {
    if (!newGroupName) return;
    
    // Create the group
    const groupId = onCreateGroup({
      name: newGroupName,
      color: newGroupColor,
      description: newGroupDescription,
    });
    
    // Add selected widgets to the group
    if (selectedWidgetIds.length > 0) {
      onAddToGroup(selectedWidgetIds, groupId, selectedParentWidgetId || undefined);
    }
    
    // Reset form and go back to groups view
    setNewGroupName('');
    setNewGroupColor(colorOptions[0].value);
    setNewGroupDescription('');
    setSelectedParentWidgetId(null);
    setActiveView('groups');
  };
  
  // Handle updating an existing group
  const handleUpdateGroup = () => {
    if (!selectedGroupId || !newGroupName) return;
    
    const updatedGroup: WidgetGroup = {
      id: selectedGroupId,
      name: newGroupName,
      color: newGroupColor,
      description: newGroupDescription,
      parentWidgetId: selectedParentWidgetId || undefined
    };
    
    onUpdateGroup(updatedGroup);
    
    // If parent widget was updated, update the widget collection
    if (selectedParentWidgetId) {
      const widgetsInGroup = widgets
        .filter(w => w.groupId === selectedGroupId)
        .map(w => w.id);
      
      if (widgetsInGroup.length > 0) {
        onAddToGroup(widgetsInGroup, selectedGroupId, selectedParentWidgetId);
      }
    }
    
    setActiveView('groups');
  };
  
  // Get list of widgets in a specific group
  const getWidgetsInGroup = (groupId: string) => {
    return widgets.filter(widget => widget.groupId === groupId);
  };
  
  // Get available widgets that can be assigned as parent (in the same group)
  const getParentCandidates = (groupId: string | null) => {
    if (!groupId) return [];
    return widgets.filter(widget => widget.groupId === groupId);
  };
  
  // Render the modal
  return createPortal(
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div 
        className="glass-card relative w-full max-w-lg mx-4 rounded-xl border border-[#47be8f]/30 max-h-[80vh] flex flex-col bg-[#1a2a2a]"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-[#47be8f]/20 bg-[#182828]">
          <h3 className="text-lg font-semibold">
            {activeView === 'create' ? 'Create Widget Group' : 
             activeView === 'edit' ? 'Edit Widget Group' : 
             'Widget Groups'}
          </h3>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-white/10 transition-colors text-[#c19411]"
          >
            <X size={18} />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-grow overflow-y-auto p-4 custom-scrollbar">
          {/* List existing groups view */}
          {activeView === 'groups' && (
            <div className="space-y-4">
              {/* Current selection info */}
              <div className="glass p-3 rounded-lg bg-[#182828] mb-4 border border-[#47be8f]/20">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Users size={16} className="text-[#47be8f]" />
                  Selected Widgets
                </h4>
                <div className="space-y-1 mb-3">
                  {selectedWidgets.length > 0 ? (
                    selectedWidgets.map(widget => (
                      <div key={widget.id} className="flex items-center gap-2 text-xs">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#47be8f]"></div>
                        <span>{widget.title}</span>
                        {widget.groupId && (
                          <span className="px-1.5 py-0.5 bg-[#1a2a2a] rounded-full text-[10px]">
                            {widgetGroups.find(g => g.id === widget.groupId)?.name}
                          </span>
                        )}
                        {widget.isParentWidget && (
                          <span className="px-1.5 py-0.5 bg-[#47be8f]/20 rounded-full text-[10px] text-[#47be8f]">
                            Parent
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 italic">No widgets selected</p>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {selectedWidgets.length > 0 && !commonGroupId && (
                    <button
                      onClick={() => setActiveView('create')}
                      className="text-xs px-2 py-1 bg-[#182828] border border-[#47be8f]/30 hover:bg-[#1c2c2c] rounded-sm flex items-center gap-1 text-[#47be8f]"
                    >
                      <Plus size={12} />
                      Create New Group
                    </button>
                  )}
                  
                  {selectedWidgets.length > 0 && commonGroupId && (
                    <button
                      onClick={() => {
                        setSelectedGroupId(commonGroupId);
                        setActiveView('edit');
                      }}
                      className="text-xs px-2 py-1 bg-[#182828] border border-[#47be8f]/30 hover:bg-[#1c2c2c] rounded-sm flex items-center gap-1 text-[#47be8f]"
                    >
                      <Check size={12} />
                      Edit Group
                    </button>
                  )}
                  
                  {selectedWidgets.length > 0 && commonGroupId && (
                    <button
                      onClick={() => onRemoveFromGroup(selectedWidgetIds)}
                      className="text-xs px-2 py-1 bg-[#182828] border border-red-500/30 hover:bg-[#1c2c2c] rounded-sm text-red-400 flex items-center gap-1"
                    >
                      <X size={12} />
                      Remove from Group
                    </button>
                  )}
                  
                  {selectedWidgets.length > 0 && !commonGroupId && widgetGroups.length > 0 && (
                    <select
                      className="text-xs px-2 py-1 bg-[#182828] border border-[#47be8f]/30 rounded-sm text-white"
                      onChange={(e) => {
                        const groupId = e.target.value;
                        if (groupId) {
                          onAddToGroup(selectedWidgetIds, groupId);
                          setSelectedGroupId(groupId);
                        }
                      }}
                      value=""
                    >
                      <option value="" disabled>Add to existing group</option>
                      {widgetGroups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              
              {/* List of existing groups */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">Widget Groups</h4>
                  <button
                    onClick={() => setActiveView('create')}
                    className="text-xs px-2 py-1 bg-gradient-to-r from-[#47be8f] to-[#c19411] text-black rounded-sm flex items-center gap-1 font-medium"
                  >
                    <Plus size={12} />
                    New Group
                  </button>
                </div>
                
                {widgetGroups.length > 0 ? (
                  <div className="space-y-2">
                    {widgetGroups.map(group => {
                      const groupWidgets = getWidgetsInGroup(group.id);
                      const parentWidget = groupWidgets.find(w => w.isParentWidget);
                      
                      return (
                        <div
                          key={group.id}
                          className="p-3 rounded-lg bg-[#182828] hover:bg-[#1c2c2c] transition-colors border border-[#47be8f]/20"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: group.color }}
                              ></div>
                              <h5 className="text-sm font-medium">{group.name}</h5>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setSelectedGroupId(group.id);
                                  setActiveView('edit');
                                }}
                                className="p-1 rounded hover:bg-[#1c2c2c] transition-colors text-[#47be8f]"
                                title="Edit group"
                              >
                                <Users size={14} />
                              </button>
                              <button
                                onClick={() => onDeleteGroup(group.id)}
                                className="p-1 rounded hover:bg-[#1c2c2c] text-red-400 transition-colors"
                                title="Delete group"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-400 mb-1">
                            {group.description || 'No description'}
                          </div>
                          
                          <div className="flex flex-wrap gap-1 items-center text-xs">
                            <span className="text-gray-400">Widgets:</span>
                            <span className="bg-[#1a2a2a] px-1.5 rounded">{groupWidgets.length}</span>
                            
                            {parentWidget && (
                              <>
                                <span className="text-gray-400 ml-2">Parent:</span>
                                <span className="bg-[#47be8f]/20 text-[#47be8f] px-1.5 rounded">{parentWidget.title}</span>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No groups created yet</p>
                )}
              </div>
            </div>
          )}
          
          {/* Create or Edit group form */}
          {(activeView === 'create' || activeView === 'edit') && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Group Name</label>
                <input
                  type="text"
                  className="w-full bg-[#182828] border border-[#47be8f]/30 rounded-lg px-3 py-2 text-sm focus:border-[#47be8f] focus:outline-none"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description (Optional)</label>
                <input
                  type="text"
                  className="w-full bg-[#182828] border border-[#47be8f]/30 rounded-lg px-3 py-2 text-sm focus:border-[#47be8f] focus:outline-none"
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder="Enter description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Group Color</label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map(color => (
                    <button
                      key={color.value}
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        newGroupColor === color.value ? 'ring-2 ring-[#c19411]' : ''
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setNewGroupColor(color.value)}
                      title={color.name}
                    >
                      {newGroupColor === color.value && (
                        <Check size={16} className={color.name === 'Yellow' ? 'text-dark-grey' : 'text-white'} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {selectedWidgetIds.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Parent Widget (Optional)</label>
                  <p className="text-xs text-gray-400 mb-2">
                    The parent widget provides data to other widgets in the group
                  </p>
                  <select
                    className="w-full bg-[#182828] border border-[#47be8f]/30 rounded-lg px-3 py-2 text-sm focus:border-[#47be8f] focus:outline-none"
                    value={selectedParentWidgetId || ''}
                    onChange={(e) => setSelectedParentWidgetId(e.target.value || null)}
                  >
                    <option value="">No parent</option>
                    {selectedWidgets.map(widget => (
                      <option key={widget.id} value={widget.id}>
                        {widget.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {activeView === 'edit' && selectedGroupId && (
                <div>
                  <label className="block text-sm font-medium mb-1">Widgets in Group</label>
                  <div className="max-h-40 overflow-y-auto p-2 bg-[#182828] rounded-lg space-y-1 custom-scrollbar">
                    {getWidgetsInGroup(selectedGroupId).map(widget => (
                      <div key={widget.id} className="flex items-center justify-between text-xs p-1.5 rounded-lg hover:bg-[#1c2c2c]">
                        <span>{widget.title}</span>
                        {widget.isParentWidget && (
                          <span className="px-1.5 py-0.5 bg-[#47be8f]/20 text-[#47be8f] rounded-full text-[10px]">
                            Parent
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer/Actions */}
        <div className="p-4 border-t border-[#47be8f]/20 flex justify-end gap-2 bg-[#182828]">
          {activeView === 'groups' ? (
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-[#182828] border border-[#47be8f]/30 hover:bg-[#1c2c2c] rounded-lg text-sm text-white"
            >
              Close
            </button>
          ) : (
            <>
              <button 
                onClick={() => setActiveView('groups')}
                className="px-4 py-2 bg-[#182828] border border-[#47be8f]/30 hover:bg-[#1c2c2c] rounded-lg text-sm text-white"
              >
                Cancel
              </button>
              <button 
                onClick={activeView === 'create' ? handleCreateGroup : handleUpdateGroup}
                className="px-4 py-2 bg-gradient-to-r from-[#47be8f] to-[#c19411] text-black rounded-lg text-sm font-medium"
                disabled={!newGroupName}
              >
                {activeView === 'create' ? 'Create Group' : 'Update Group'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WidgetGroupManager;